// src/pages/Dashboard.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BarChart2, Building2, CheckCircle, XCircle,
  FileText, Clock, Zap, ArrowRight, Upload,
} from 'lucide-react';
import { getStats, getReports, getHealth } from '../utils/api';

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: accent } : undefined}>{value ?? '—'}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function RecentReportRow({ report, navigate }) {
  const ext = (report.outputExt || report.outputFilename?.split('.').pop() || 'txt').replace('.', '');
  return (
    <div
      onClick={() => navigate('/reports')}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
        border: '1px solid var(--border)', background: 'var(--navy-3)',
        transition: 'border-color 0.15s',
        marginBottom: 8,
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(226,185,111,0.35)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
        background: 'rgba(226,185,111,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <FileText size={16} style={{ color: 'var(--gold)' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {report.inputFilename}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {report.promptName}
          {report.tokensUsed ? ` · ${report.tokensUsed.toLocaleString()} tokens` : ''}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{
          fontSize: 11, padding: '2px 8px', borderRadius: 20, marginBottom: 4,
          background: 'rgba(63,185,80,0.12)', color: 'var(--green)', fontWeight: 600,
        }}>
          .{ext}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {new Date(report.processedAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const { data: stats }   = useQuery({ queryKey: ['stats'],   queryFn: getStats,          refetchInterval: 15000 });
  const { data: health }  = useQuery({ queryKey: ['health'],  queryFn: getHealth,         refetchInterval: 30000 });
  const { data: reports } = useQuery({ queryKey: ['reports'], queryFn: () => getReports(5), refetchInterval: 15000 });

  const recentReports = reports?.reports?.slice(0, 3) || [];
  const lastReport    = recentReports[0];

  const HEALTH_LABELS = {
    server:          'API Server',
    anthropicApiKey: 'Claude API Key',
    dextApiKey:      'Dext API Key',
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          Dext Bank Statement Analysis — powered by Claude AI
        </p>
      </div>

      {/* ── Stats ── */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Total Analyses" value={stats?.totalRuns ?? 0} sub="All time" />
        <StatCard label="Succeeded" value={stats?.succeeded ?? 0} sub="Reports saved" accent="var(--green)" />
        <StatCard label="Failed" value={stats?.failed ?? 0} sub="Errors" accent={stats?.failed > 0 ? 'var(--red)' : undefined} />
        <StatCard label="Tokens Used" value={stats?.totalTokensUsed?.toLocaleString() ?? 0} sub={`≈ $${stats?.estimatedCost ?? '0.00'} total`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

        {/* ── Quick actions ── */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card-title" style={{ marginBottom: 6 }}>Quick Actions</div>

          <button
            className="btn btn-primary"
            style={{ justifyContent: 'flex-start', gap: 10 }}
            onClick={() => navigate('/dext')}
          >
            <Upload size={15} />
            <span>Upload Dext CSV Export</span>
            <ArrowRight size={13} style={{ marginLeft: 'auto' }} />
          </button>

          <button
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', gap: 10 }}
            onClick={() => navigate('/reports')}
          >
            <BarChart2 size={15} />
            <span>View Analyzed Reports</span>
            <ArrowRight size={13} style={{ marginLeft: 'auto' }} />
          </button>

          <button
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', gap: 10 }}
            onClick={() => navigate('/prompts')}
          >
            <Zap size={15} />
            <span>Edit CFO Prompt</span>
            <ArrowRight size={13} style={{ marginLeft: 'auto' }} />
          </button>
        </div>

        {/* ── System Health ── */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>System Health</div>
          {health ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(health.checks || {}).map(([key, ok]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: (ok === true || ok === 'ok') ? 'var(--green)' : 'var(--red)',
                    boxShadow: (ok === true || ok === 'ok') ? '0 0 6px var(--green)' : '0 0 6px var(--red)',
                  }} />
                  <span style={{ flex: 1, fontSize: 13 }}>{HEALTH_LABELS[key] || key}</span>
                  {(ok === true || ok === 'ok')
                    ? <CheckCircle size={14} color="var(--green)" />
                    : <XCircle size={14} color="var(--red)" />}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
              <span className="spinner" /> Checking…
            </div>
          )}
        </div>
      </div>

      {/* ── Last analysis summary ── */}
      {lastReport && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={15} style={{ color: 'var(--gold)' }} />
            Last Analysis
          </div>
          <div style={{
            background: 'rgba(226,185,111,0.06)', border: '1px solid rgba(226,185,111,0.2)',
            borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: 'var(--gold)' }}>
                {lastReport.inputFilename}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <span>{lastReport.promptName}</span>
                {lastReport.tokensUsed ? <span> · {lastReport.tokensUsed.toLocaleString()} tokens used</span> : null}
                <span> · {new Date(lastReport.processedAt).toLocaleString()}</span>
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/reports')}>
              <BarChart2 size={13} /> View Report
            </button>
          </div>
        </div>
      )}

      {/* ── Recent analyses list ── */}
      {recentReports.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Building2 size={15} style={{ color: 'var(--gold)' }} />
              Recent Analyses
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => navigate('/reports')}
            >
              View all <ArrowRight size={11} />
            </button>
          </div>
          {recentReports.map(r => (
            <RecentReportRow key={r.id} report={r} navigate={navigate} />
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {recentReports.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <BarChart2 size={36} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No analyses yet</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
            Export a CSV from Dext → Bank → Transactions → Export all, then upload it here.
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/dext')}>
            <Upload size={14} /> Upload Dext CSV
          </button>
        </div>
      )}
    </div>
  );
}
